/** The read envelope, the stream frames, projection health, the session view and API errors (V08). */
import {
  ContractViolation,
  bool,
  exact,
  field,
  instant,
  int,
  intMap,
  listOf,
  nat,
  nullable,
  oneOf,
  record,
  str,
  type Reader,
} from "./decode.ts";

export const ENVIRONMENTS = ["DEVELOPMENT", "TEST", "PRODUCTION"] as const;
export const MODES = ["RESEARCH", "SHADOW", "PAPER", "ASSISTED", "LIVE"] as const;
export const FRESHNESS = ["FRESH", "STALE", "UNAVAILABLE"] as const;
export const RESYNC_REASONS = [
  "GAP",
  "SCHEMA",
  "CURSOR_EXPIRED",
  "QUERY",
  "SCOPE",
  "SNAPSHOT",
  "PROJECTION_EPOCH",
  "AUTHORISATION",
  "AUTHORISATION_EPOCH",
  "REVOKED",
  "BUFFER_OVERFLOW",
  "GENERATION_SUPERSEDED",
  "CLOSED",
] as const;

export type Environment = (typeof ENVIRONMENTS)[number];
export type Mode = (typeof MODES)[number];
export type Freshness = (typeof FRESHNESS)[number];
export type ResyncReason = (typeof RESYNC_REASONS)[number];

/** One page of a read. */
export interface Page<R> {
  readonly rows: readonly R[];
  readonly nextCursor: string | null;
  readonly pageRows: number;
  readonly hasMore: boolean;
}

/** What every read answers (master §10.3; V08-A04). */
export interface ReadEnvelope<R> {
  readonly projection: string;
  readonly schemaVersion: number;
  readonly environment: Environment;
  readonly mode: Mode | null;
  readonly accountId: string | null;
  readonly snapshotId: string;
  readonly watermark: number;
  readonly entityVersions: ReadonlyMap<string, number>;
  readonly sourceTime: string | null;
  readonly availableTime: string | null;
  readonly snapshotTime: string;
  readonly freshness: Freshness;
  readonly ageUs: number | null;
  readonly freshnessBoundUs: number;
  readonly actionsEnabled: boolean;
  readonly superseded: boolean;
  readonly viewGeneration: number;
  readonly correlationId: string;
  readonly page: Page<R>;
}

const ENVELOPE_KEYS = [
  "projection",
  "schema_version",
  "environment",
  "mode",
  "account_id",
  "snapshot_id",
  "watermark",
  "entity_versions",
  "source_time",
  "available_time",
  "snapshot_time",
  "freshness",
  "age_us",
  "freshness_bound_us",
  "actions_enabled",
  "superseded",
  "view_generation",
  "correlation_id",
  "page",
] as const;

const PAGE_KEYS = ["rows", "next_cursor", "page_rows", "has_more"] as const;

function readPage<R>(row: Reader<R>): Reader<Page<R>> {
  return (value, path) => {
    const found = exact(value, path, PAGE_KEYS);
    const page: Page<R> = {
      rows: field(found, path, "rows", listOf(row)),
      nextCursor: field(found, path, "next_cursor", nullable(str)),
      pageRows: field(found, path, "page_rows", nat),
      hasMore: field(found, path, "has_more", bool),
    };
    if (page.pageRows < 1 || page.rows.length > page.pageRows) {
      throw new ContractViolation(path, "at most page_rows rows");
    }
    return page;
  };
}

function checkEnvelope(envelope: ReadEnvelope<unknown>, path: string): void {
  const { sourceTime, availableTime, snapshotTime } = envelope;
  if ((sourceTime === null) !== (availableTime === null)) {
    throw new ContractViolation(path, "source_time and available_time both present or both absent");
  }
  if (
    sourceTime !== null &&
    availableTime !== null &&
    !(sourceTime <= availableTime && availableTime <= snapshotTime)
  ) {
    throw new ContractViolation(path, "source_time <= available_time <= snapshot_time");
  }
  if ((envelope.ageUs === null) !== (envelope.freshness === "UNAVAILABLE")) {
    throw new ContractViolation(path, "age_us absent exactly when UNAVAILABLE");
  }
  if (envelope.actionsEnabled !== (envelope.freshness === "FRESH" && !envelope.superseded)) {
    throw new ContractViolation(path, "actions_enabled exactly when FRESH and not superseded");
  }
  if (envelope.entityVersions.size !== envelope.page.rows.length) {
    throw new ContractViolation(path, "one entity version per row");
  }
}

/** Decode a read envelope, reading each page row with `row`; throws `ContractViolation`. */
export function decodeEnvelope<R>(
  value: unknown,
  row: Reader<R>,
  path = "envelope",
): ReadEnvelope<R> {
  const found = exact(value, path, ENVELOPE_KEYS);
  const envelope: ReadEnvelope<R> = {
    projection: field(found, path, "projection", str),
    schemaVersion: field(found, path, "schema_version", nat),
    environment: field(found, path, "environment", oneOf(ENVIRONMENTS)),
    mode: field(found, path, "mode", nullable(oneOf(MODES))),
    accountId: field(found, path, "account_id", nullable(str)),
    snapshotId: field(found, path, "snapshot_id", str),
    watermark: field(found, path, "watermark", nat),
    entityVersions: field(found, path, "entity_versions", intMap),
    sourceTime: field(found, path, "source_time", nullable(instant)),
    availableTime: field(found, path, "available_time", nullable(instant)),
    snapshotTime: field(found, path, "snapshot_time", instant),
    freshness: field(found, path, "freshness", oneOf(FRESHNESS)),
    ageUs: field(found, path, "age_us", nullable(nat)),
    freshnessBoundUs: field(found, path, "freshness_bound_us", nat),
    actionsEnabled: field(found, path, "actions_enabled", bool),
    superseded: field(found, path, "superseded", bool),
    viewGeneration: field(found, path, "view_generation", nat),
    correlationId: field(found, path, "correlation_id", str),
    page: field(found, path, "page", readPage(row)),
  };
  checkEnvelope(envelope, path);
  return envelope;
}

/** A row nobody has typed yet: a plain object. */
export type RawRow = Record<string, unknown>;

/** Decode a read envelope whose rows are left as plain objects. */
export function decodeReadEnvelope(value: unknown): ReadEnvelope<RawRow> {
  return decodeEnvelope(value, record);
}

/** One stream event for a subscription (`event: <projection>`). */
export interface StreamEvent {
  readonly subscriptionId: string;
  readonly sequence: number;
  readonly entityId: string;
  readonly version: number;
  readonly schemaVersion: number;
  readonly data: RawRow;
}

/** Decode a stream event frame's data. */
export function decodeStreamEvent(value: unknown): StreamEvent {
  const path = "event";
  const found = exact(value, path, [
    "subscription_id",
    "sequence",
    "entity_id",
    "version",
    "schema_version",
    "data",
  ]);
  const sequence = field(found, path, "sequence", int);
  const version = field(found, path, "version", int);
  if (sequence < 1 || version < 1) {
    throw new ContractViolation(path, "sequence and version >= 1");
  }
  return {
    subscriptionId: field(found, path, "subscription_id", str),
    sequence,
    entityId: field(found, path, "entity_id", str),
    version,
    schemaVersion: field(found, path, "schema_version", nat),
    data: field(found, path, "data", record),
  };
}

/** Decode the `event: resync` frame's data; returns the reason. */
export function decodeResyncFrame(value: unknown): ResyncReason {
  const found = exact(value, "resync", ["reason", "resync"]);
  if (found.reason !== "RESYNC_REQUIRED") {
    throw new ContractViolation("resync.reason", "RESYNC_REQUIRED");
  }
  return field(found, "resync", "resync", oneOf(RESYNC_REASONS));
}

/** A subscription request as the handshake carries it. */
export interface SubscriptionRequest {
  readonly projection: string;
  readonly accountId: string;
  readonly schemaVersion: number;
  readonly snapshotId: string;
  readonly afterWatermark: number;
}

/** Decode a subscription request. */
export function decodeSubscriptionRequest(value: unknown): SubscriptionRequest {
  const path = "subscription";
  const found = exact(value, path, [
    "projection",
    "account_id",
    "schema_version",
    "snapshot_id",
    "after_watermark",
  ]);
  return {
    projection: field(found, path, "projection", str),
    accountId: field(found, path, "account_id", str),
    schemaVersion: field(found, path, "schema_version", nat),
    snapshotId: field(found, path, "snapshot_id", str),
    afterWatermark: field(found, path, "after_watermark", nat),
  };
}

/** What a subscription is bound to (server side; decoded for the contract round trip). */
export interface Binding {
  readonly projection: string;
  readonly permission: string;
  readonly queryHash: string;
  readonly schemaVersion: number;
  readonly authorisationEpoch: readonly [number, number];
  readonly viewGeneration: number;
  readonly projectionEpoch: number;
  readonly coalescing: boolean;
}

/** Decode a stream binding. */
export function decodeBinding(value: unknown): Binding {
  const path = "binding";
  const found = exact(value, path, [
    "projection",
    "scope",
    "permission",
    "query_hash",
    "schema_version",
    "authorisation_epoch",
    "view_generation",
    "projection_epoch",
    "coalescing",
  ]);
  field(found, path, "scope", (scope, where) =>
    exact(scope, where, [
      "workspace_id",
      "environment",
      "account_id",
      "kind",
      "owner",
      "target_identity_id",
    ]),
  );
  const epoch = field(found, path, "authorisation_epoch", listOf(nat));
  const [entitlement, security] = epoch;
  if (epoch.length !== 2 || entitlement === undefined || security === undefined) {
    throw new ContractViolation(
      `${path}.authorisation_epoch`,
      "[entitlement_version, security_epoch]",
    );
  }
  return {
    projection: field(found, path, "projection", str),
    permission: field(found, path, "permission", str),
    queryHash: field(found, path, "query_hash", str),
    schemaVersion: field(found, path, "schema_version", nat),
    authorisationEpoch: [entitlement, security],
    viewGeneration: field(found, path, "view_generation", nat),
    projectionEpoch: field(found, path, "projection_epoch", nat),
    coalescing: field(found, path, "coalescing", bool),
  };
}

/** One projection's epoch and lag. */
export interface ProjectionHealth {
  readonly projection: string;
  readonly epoch: number;
  readonly appliedLagUs: number | null;
  readonly freshnessBoundUs: number;
  readonly freshness: Freshness;
}

/** What the server advertises beyond the Python health read (wave-3 extension). */
export interface Capabilities {
  readonly rum: boolean;
  readonly routingEnabled: boolean;
}

/** The health read: projections plus the capabilities (all `false` when not advertised). */
export interface HealthReport {
  readonly projections: readonly ProjectionHealth[];
  readonly capabilities: Capabilities;
}

function readHealthRow(value: unknown, path: string): ProjectionHealth {
  const found = exact(value, path, [
    "projection",
    "epoch",
    "applied_lag_us",
    "freshness_bound_us",
    "freshness",
  ]);
  return {
    projection: field(found, path, "projection", str),
    epoch: field(found, path, "epoch", nat),
    appliedLagUs: field(found, path, "applied_lag_us", nullable(nat)),
    freshnessBoundUs: field(found, path, "freshness_bound_us", nat),
    freshness: field(found, path, "freshness", oneOf(FRESHNESS)),
  };
}

/** Decode `GET /api/v1/health/projections`; `capabilities` is optional and defaults to all off. */
export function decodeProjectionHealth(value: unknown): HealthReport {
  const path = "health";
  const found = record(value, path);
  const keys = "capabilities" in found ? ["projections", "capabilities"] : ["projections"];
  exact(found, path, keys);
  const projections = field(found, path, "projections", listOf(readHealthRow));
  if (!("capabilities" in found)) {
    return { projections, capabilities: { rum: false, routingEnabled: false } };
  }
  const capabilities = field(found, path, "capabilities", (inner, where) => {
    const caps = exact(inner, where, ["rum", "routing_enabled"]);
    return {
      rum: field(caps, where, "rum", bool),
      routingEnabled: field(caps, where, "routing_enabled", bool),
    };
  });
  return { projections, capabilities };
}

/** The public session view (`GET /auth/session`). */
export interface SessionView {
  readonly identityId: string;
  readonly environment: Environment;
  readonly createdAt: string;
  readonly lastActivityAt: string;
  readonly inactivityExpiresAt: string;
  readonly absoluteExpiryAt: string;
  readonly entitlementVersion: number;
}

/** Decode the session view. */
export function decodeSessionView(value: unknown): SessionView {
  const path = "session";
  const found = exact(value, path, [
    "identity_id",
    "environment",
    "created_at",
    "last_activity_at",
    "inactivity_expires_at",
    "absolute_expiry_at",
    "entitlement_version",
  ]);
  return {
    identityId: field(found, path, "identity_id", str),
    environment: field(found, path, "environment", oneOf(ENVIRONMENTS)),
    createdAt: field(found, path, "created_at", instant),
    lastActivityAt: field(found, path, "last_activity_at", instant),
    inactivityExpiresAt: field(found, path, "inactivity_expires_at", instant),
    absoluteExpiryAt: field(found, path, "absolute_expiry_at", instant),
    entitlementVersion: field(found, path, "entitlement_version", nat),
  };
}

/** An API refusal body: a reason code, a kind (never a value) and the correlation id. */
export interface ApiErrorBody {
  readonly reason: string;
  readonly kind: string | readonly string[] | null;
  readonly correlationId: string;
  readonly resync: ResyncReason | null;
}

/** Decode a refusal body (`kind` and `resync` are optional on the wire). */
export function decodeApiError(value: unknown): ApiErrorBody {
  const path = "error";
  const found = record(value, path);
  const allowed = ["reason", "kind", "correlation_id", "resync"];
  for (const key of Object.keys(found)) {
    if (!allowed.includes(key)) {
      throw new ContractViolation(`${path}.${key}`, "no such field");
    }
  }
  return {
    reason: field(found, path, "reason", str),
    kind:
      found.kind === undefined
        ? null
        : field(found, path, "kind", (kind, where) =>
            Array.isArray(kind) ? listOf(str)(kind, where) : str(kind, where),
          ),
    correlationId: field(found, path, "correlation_id", str),
    resync: found.resync === undefined ? null : field(found, path, "resync", oneOf(RESYNC_REASONS)),
  };
}
